/**
 * Lead tracking utilities — capture ad/analytics params for offline conversion
 * tracking and CRM integration.
 *
 * On first page load, URL query params (gclid, gbraid, wbraid, UTMs) are
 * saved to sessionStorage so they survive SPA navigation. When a lead form
 * is submitted, getTrackingFields() returns all captured values to include
 * in the API payload.
 */

const STORAGE_KEY = "gf_lead_tracking";

const TRACKED_PARAMS = [
  "gclid",
  "gbraid",
  "wbraid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];

const BRAND = "giant_fitness";

/**
 * Call once on app mount. Captures URL query params and landing page into
 * sessionStorage so they persist across SPA route changes.
 */
export function initLeadTracking() {
  if (typeof window === "undefined" || typeof sessionStorage === "undefined") return;

  try {
    const existing = sessionStorage.getItem(STORAGE_KEY);
    if (existing) return; // already captured for this session

    const params = new URLSearchParams(window.location.search);
    const tracked = {};
    for (const key of TRACKED_PARAMS) {
      const val = params.get(key);
      if (val) tracked[key] = val;
    }

    tracked.landing_page = window.location.pathname + window.location.search;
    tracked.referrer = document.referrer || "";

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tracked));
  } catch {
    /* sessionStorage unavailable (private mode, quota) */
  }
}

/**
 * Returns tracking fields to attach to a lead submission payload.
 * Safe to call at any time — returns empty strings for missing values.
 */
export function getTrackingFields() {
  const defaults = {
    brand: BRAND,
    gclid: "",
    gbraid: "",
    wbraid: "",
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
    utm_term: "",
    utm_content: "",
    landing_page: "",
    page_url: "",
    referrer: "",
  };

  if (typeof window !== "undefined") {
    defaults.page_url = window.location.pathname + window.location.search;
  }

  if (typeof sessionStorage === "undefined") return defaults;

  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return defaults;
    const stored = JSON.parse(raw);
    if (!stored || typeof stored !== "object") return defaults;

    for (const key of TRACKED_PARAMS) {
      if (stored[key]) defaults[key] = stored[key];
    }
    if (stored.landing_page) defaults.landing_page = stored.landing_page;
    if (stored.referrer) defaults.referrer = stored.referrer;
  } catch {
    /* ignore */
  }

  return defaults;
}
